import { useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE =
  'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * The single dialog implementation for the site, lifted from the log
 * detail modal — which was the only correct one. Gives every consumer:
 * role/aria-modal, a Tab focus trap, Escape to close, focus moved in on
 * open and returned to the trigger on close, and a body scroll lock.
 *
 * Callers own the trigger; `onClose` is expected to restore focus by
 * keeping a ref to it (see `useDialog`).
 */
const Dialog = ({
  title,
  onClose,
  children,
  variant = '',
  labelledBy,
  closeLabel = 'Close',
}) => {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const generatedId = useId();
  const titleId = labelledBy || generatedId;

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  useEffect(() => {
    closeRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusables = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE));
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="dialog-overlay" onClick={onClose}>
      <div
        ref={dialogRef}
        className={`dialog ${variant}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeRef}
          type="button"
          className="dialog__close"
          onClick={onClose}
          aria-label={closeLabel}
        >
          &#10005;
        </button>

        {/* A dialog must always have an accessible name. When the caller
            renders its own heading it passes `labelledBy` instead, and
            this hidden node is skipped. */}
        {!labelledBy && (
          title
            ? <h2 className="dialog__title" id={titleId}>{title}</h2>
            : <span className="sr-only" id={titleId}>Dialog</span>
        )}

        {children}
      </div>
    </div>,
    document.body
  );
};

export default Dialog;
